import { useSyncExternalStore } from 'react';
import { queryClient } from '@/app/providers/query-client';
import type { ImageUpscaleRequestTarget } from '@/lib/storyboard-image-upscale';
import type { NormalizedGenerationProgressState } from '@/lib/preview-generation';
import { useAppStore } from '@/stores/appStore';
import { useOrgContextStore } from '@/stores/orgContextStore';
import { invalidateResourceImageTaskCaches } from '@/lib/project-resource-cache-invalidation';
import { invalidateStoryboardImagePlanQueries } from '@/hooks/api/useStoryboardPlans';

export interface PendingImageUpscaleJob {
  jobId: string;
  target: ImageUpscaleRequestTarget;
  projectId?: string;
  resourceId?: string | null;
  previewItemId?: string | null;
  aspectRatio?: number;
  progressState?: NormalizedGenerationProgressState;
}

const listeners = new Set<() => void>();
const jobsByScope = new Map<string, PendingImageUpscaleJob[]>();
const EMPTY_JOBS: PendingImageUpscaleJob[] = [];
const STORAGE_PREFIX = 'jointo:image-upscale-tasks:v1:';

export function getImageUpscaleTaskScope(): string | null {
  const userId = useAppStore.getState().user?.userId;
  return userId
    ? `${encodeURIComponent(userId)}:${encodeURIComponent(useOrgContextStore.getState().activeOrgId ?? '__personal__')}`
    : null;
}

function isPendingJob(value: unknown): value is PendingImageUpscaleJob {
  if (!value || typeof value !== 'object') return false;
  const { jobId, target } = value as Partial<PendingImageUpscaleJob>;
  if (typeof jobId !== 'string' || !jobId.trim() || !target) return false;
  const has = (...values: unknown[]) =>
    values.every((item) => typeof item === 'string' && Boolean(item.trim()));
  if (target.kind === 'storyboard-image') {
    return has(target.storyboardId, target.planId, target.sourceImageId);
  }
  if (target.kind === 'footage-file') return has(target.projectId, target.sourceResourceId);
  return (
    target.kind === 'project-resource' &&
    has(target.projectId, target.sourceResourceId, target.elementTagId) &&
    ['character', 'location', 'prop'].includes(target.resourceType)
  );
}

function writeJobs(scope: string, jobs: PendingImageUpscaleJob[]): void {
  jobsByScope.set(scope, jobs);
  try {
    // 仅保存恢复所需身份，进度以服务端重新查询的结果为准。
    window.sessionStorage.setItem(
      STORAGE_PREFIX + scope,
      JSON.stringify(
        jobs.map(({ jobId, target, projectId, resourceId, previewItemId, aspectRatio }) => ({
          jobId,
          target,
          projectId,
          resourceId,
          previewItemId,
          aspectRatio,
        }))
      )
    );
  } catch {
    // 浏览器禁止存储时仍保留当前页面的任务展示。
  }
  emitPendingImageUpscaleJobs();
}

function emitPendingImageUpscaleJobs() {
  listeners.forEach((listener) => listener());
}

export function getPendingImageUpscaleJobs(
  scope = getImageUpscaleTaskScope()
): PendingImageUpscaleJob[] {
  if (!scope) return EMPTY_JOBS;
  const current = jobsByScope.get(scope);
  if (current) return current;
  let jobs: PendingImageUpscaleJob[] = [];
  try {
    const stored: unknown = JSON.parse(
      window.sessionStorage.getItem(STORAGE_PREFIX + scope) ?? '[]'
    );
    if (Array.isArray(stored)) jobs = stored.filter(isPendingJob);
  } catch {
    // 单个会话记录损坏不影响提交新任务。
  }
  jobsByScope.set(scope, jobs);
  return jobs;
}

export function registerPendingImageUpscaleJob(
  job: PendingImageUpscaleJob,
  scope = getImageUpscaleTaskScope()
): void {
  const jobId = job.jobId.trim();
  if (!scope || !isPendingJob(job)) return;
  writeJobs(scope, [
    ...getPendingImageUpscaleJobs(scope).filter((item) => item.jobId !== jobId),
    { ...job, jobId },
  ]);
}

export function removePendingImageUpscaleJob(
  jobId: string,
  scope = getImageUpscaleTaskScope()
): void {
  if (!scope) return;
  const pendingJobs = getPendingImageUpscaleJobs(scope);
  const next = pendingJobs.filter((item) => item.jobId !== jobId);
  if (next.length === pendingJobs.length) return;
  writeJobs(scope, next);
}

export function updateImageUpscaleProgress(
  jobId: string,
  progressState: NormalizedGenerationProgressState,
  scope = getImageUpscaleTaskScope()
): void {
  if (!scope) return;
  const jobs = getPendingImageUpscaleJobs(scope);
  const job = jobs.find((item) => item.jobId === jobId);
  if (!job || JSON.stringify(job.progressState) === JSON.stringify(progressState)) return;
  jobsByScope.set(
    scope,
    jobs.map((item) => (item.jobId === jobId ? { ...item, progressState } : item))
  );
  emitPendingImageUpscaleJobs();
}

export function usePendingImageUpscaleJobs(): PendingImageUpscaleJob[] {
  const userId = useAppStore((state) => state.user?.userId);
  const orgId = useOrgContextStore((state) => state.activeOrgId);
  const scope = userId
    ? `${encodeURIComponent(userId)}:${encodeURIComponent(orgId ?? '__personal__')}`
    : null;
  return useSyncExternalStore(subscribePendingImageUpscaleJobs, () =>
    getPendingImageUpscaleJobs(scope)
  );
}

export function subscribePendingImageUpscaleJobs(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export async function invalidateImageUpscaleCaches(
  target: ImageUpscaleRequestTarget,
  context?: Pick<PendingImageUpscaleJob, 'projectId' | 'resourceId'>
): Promise<void> {
  if (target.kind === 'storyboard-image') {
    invalidateStoryboardImagePlanQueries(queryClient, target.storyboardId);
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['storyboard-images', target.storyboardId] }),
      queryClient.invalidateQueries({ queryKey: ['storyboard-plans'] }),
      queryClient.invalidateQueries({ queryKey: ['storyboards'] }),
      ...(context?.projectId
        ? [queryClient.invalidateQueries({ queryKey: ['project-element-tags', context.projectId] })]
        : []),
    ]);
    return;
  }
  if (target.kind === 'project-resource' && context?.resourceId) {
    await invalidateResourceImageTaskCaches(queryClient, {
      permissionProjectId: target.permissionProjectId ?? context.projectId ?? target.projectId,
      resourceProjectId: target.projectId,
      resourceId: context.resourceId,
      tagId: target.elementTagId,
      mode:
        target.resourceType === 'character'
          ? 'character-image'
          : target.resourceType === 'location'
            ? 'location-image'
            : 'prop-image',
      refreshElementPresentation: true,
    });
  }
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ['resource-library', 'v1'] }),
    queryClient.invalidateQueries({ queryKey: ['project-resources'] }),
    queryClient.invalidateQueries({ queryKey: ['project-element-tags'] }),
    queryClient.invalidateQueries({ queryKey: ['storyboard-items'] }),
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey;
        return (
          key[0] === 'canvas-snapshot' &&
          [target.projectId, target.permissionProjectId, context?.projectId].includes(
            String(key[1])
          ) &&
          key[6] === 'history'
        );
      },
    }),
    ...(target.kind === 'project-resource'
      ? [
          queryClient.invalidateQueries({
            queryKey: [
              'resource-library',
              'v1',
              'tag-resources',
              target.projectId,
              target.elementTagId,
            ],
          }),
        ]
      : []),
  ]);
}
